// Visitor & Contractor manager — route shell (RON-5466).
//
// Owns the app-shell chrome, the access gate, the visits data and the `screen` state machine.
// The prototype defines 19 screens; they are driven from the `screen` value here rather than
// ~19 routes, mirroring both the reducer in the prototype and the existing `NewBookingUI` step
// pattern.
//
// LAYOUT: built from the app's existing shell classes so it matches every other screen —
// `content-area` > inline `<header className="header">` > `component-area` > Sidebar >
// `pt-96 pb-96 my-container active-cont` > `container mw-100 pt-30`. The shared
// `Header/HeaderNoDropdown` component is NOT used: it is imported-but-commented-out on every
// screen in this app, and each screen inlines its own header instead.
//
// BEM (`visitor__*`, `visitor-list__*`) is reserved for new visitor-specific elements; the shell
// keeps the house class names.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Sidebar from "../Sidebar/Sidebar.jsx";
import NotificationPanel from "../Common/Notification.jsx";
import { Images } from "../Common/Images.jsx";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import {
  CancelVisit_api,
  CreateVisit_api,
  DEFAULT_PAGE_SIZE,
  RequestContractorVetting_api,
  UpdateVisit_api,
  // Aliased: the component has carried its own `fetchVisits` / `fetchSites` reload callbacks
  // since it was written, and two things with one name in one file is a trap.
  fetchVisits as fetchVisitList,
  fetchVisitorSites as fetchSiteList,
  isSuccessfulResponse,
  // Aliased too: this component already has a `lookupContractor` of its own, and without the
  // alias the local one shadowed the import and called ITSELF — an infinite recursion the linter
  // cannot see, because a shadowed name is perfectly legal.
  lookupContractor as lookupContractorApi,
} from "./VisitorAPI.jsx";
import { bulkEditFromApi, groupCancelFromApi } from "./visitorApiMapper.js";
import ContractorInviteWizard from "./components/ContractorInviteWizard.jsx";
import ContractorRequestVetting from "./components/ContractorRequestVetting.jsx";
import VisitorBulkEditScreen from "./components/VisitorBulkEditScreen.jsx";
import VisitorCancelConfirm from "./components/VisitorCancelConfirm.jsx";
import VisitorDetail from "./components/VisitorDetail.jsx";
import VisitorList from "./components/VisitorList.jsx";
import VisitorConfirmation from "./components/VisitorConfirmation.jsx";
import VisitorVettingDeclinedModal from "./components/VisitorVettingDeclinedModal.jsx";
import VisitorInviteWizard from "./components/VisitorInviteWizard.jsx";
import { newIdempotencyKey } from "./visitorApiMapper.js";
import {
  VETTING_REQUEST_REASON,
  VISIT_KIND,
  canCancelVisit,
  getVettingRequestedMessageKey,
  pluralKey,
} from "./visitorConstants.js";
import { toISODate } from "./visitorDateUtils.js";
import useVisitorAccess from "./useVisitorAccess.js";

// How long to wait after the last keystroke before asking the server (§4.4 search). One request
// per letter of "sarah" is five round trips for one intention; 350ms swallows ordinary typing
// without feeling laggy.
const SEARCH_DEBOUNCE_MS = 350;

// Ceiling on the page size used to re-read the list after a mutation, so a host who has scrolled a
// long way does not turn one cancel into an unbounded request the server may refuse.
const MAX_REFRESH_PAGE_SIZE = 100;

const getFirstName = () => localStorage.getItem("firstname");

const getHostName = () => {
  const first = localStorage.getItem("firstname") || "";
  const last = localStorage.getItem("lastname") || "";

  return `${first} ${last}`.trim();
};

// Screens are added phase by phase. Only the contractor wizard (Phase 4) is still a placeholder.
export const SCREENS = {
  LIST: "list",
  INVITE_VISITOR: "inviteVisitor",
  INVITE_CONTRACTOR: "inviteContractor",
  VIEW: "view",
  EDIT: "edit",
  CANCEL: "cancel",
  BULK_EDIT: "bulkEdit",
  BULK_CANCEL: "bulkCancel",
  VISITOR_INVITED: "visitorInvited",
  CHANGES_SAVED: "changesSaved",
  VISITS_CANCELLED: "visitsCancelled",
  REQUEST_VETTING: "requestVetting",
  VETTING_REQUESTED: "vettingRequested",
};

// Every screen is built. The placeholder branch stays as a safety net for an unrecognised
// `screen` value rather than for planned work.
const SCREEN_TITLE_KEYS = {};

// Headings added by the shell. VIEW is absent on purpose: the guest's name is that screen's
// heading, so a "Visit details" title above it would just repeat the card below.
const SCREEN_HEADING_KEYS = {
  [SCREENS.REQUEST_VETTING]: "react_visitors_request_vetting",
  [SCREENS.CANCEL]: "react_visitors_cancel_booking",
  [SCREENS.BULK_CANCEL]: "react_visitors_cancel_bookings",
  [SCREENS.BULK_EDIT]: "react_visitors_edit_details",
};

const VisitorUI = () => {
  const { t } = useTranslation("common");
  const {
    canSeeVisitors,
    canInviteVisitor,
    canInviteContractor,
    canAssignDesk,
    canAssignParking,
  } = useVisitorAccess();
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);

  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [screen, setScreen] = useState(SCREENS.LIST);
  const [visits, setVisits] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Server-side search + lazy loading (§4.4). `query` is what the host has typed; `appliedQuery`
  // is what the last request actually asked for. They differ for the length of the debounce, and
  // the list uses that gap to say "searching" rather than showing stale rows as if they matched.
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // This host’s DECLINED vetting requests. The ONLY notice they get — §4.10 sends no email
  // on a decline, so if this is not on screen the request simply vanishes.
  const [vettingRequests, setVettingRequests] = useState([]);
  // The one the host opened from the list, or null. A popup rather than a screen: it is a
  // thing to read, not a place to be, and sending them to a screen would lose their scroll
  // position in the list they were working through.
  const [declinedInView, setDeclinedInView] = useState(null);

  // Phase 3 selection. `selectedId` drives view/edit/cancel; `selectedIds` drives the bulk
  // screens. Ids rather than whole rows on purpose: after a re-fetch the row objects are new, and
  // a stored copy would show stale values the moment anything changed.
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [includeGroup, setIncludeGroup] = useState(false);
  // §8/§9 — "Send cancellation email". ON by default, matching the server's own default: leaving
  // the flag out sends the email, so the screen must not start in a state that contradicts it.
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  // What the last cancel actually did, so the confirmation can be specific instead of vague.
  const [cancelSummary, setCancelSummary] = useState(null);

  // What a BULK edit actually managed. `null` for a single or contractor edit, which is one
  // request and either works or does not — so that screen reads exactly as it always has.
  const [editSummary, setEditSummary] = useState(null);

  // The ticked rows a bulk cancel COULD NOT cancel. They go back on the list still ticked, so
  // the host can read why and act on them rather than hunting for them again among a hundred
  // rows — which is what the §9a walkthrough asks for, and the whole reason `results` exists.
  //
  // Held separately from `selectedIds` because the confirmation screen sits in between, and
  // `backToList` clears the selection for every other screen in the module.
  const [keepTicked, setKeepTicked] = useState([]);

  // Phase 4 contractor lookup. `hasSearched` is tracked separately from `contractor` because
  // "searched and found nobody" and "not searched yet" are different screens — the first offers to
  // request vetting, the second must not.
  const [contractor, setContractor] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  // §4.3's verdict, kept beside the record it describes. `null` means the lookup gave no
  // opinion — an older reply, or a record that came from the visit's own snapshot — and the
  // wizard falls back to deriving it rather than reading that as 'proceed'.
  const [lookup, setLookup] = useState(null);
  const [vettingEmail, setVettingEmail] = useState("");
  // WHAT THE SERVER MADE OF IT. `type` comes back on the reply (§4.10) and is decided from the
  // register — we never send it. It was a value this screen GUESSED at click time, formed
  // before the request was made, which is one answer too many for a question only the
  // register can settle.
  const [vettingType, setVettingType] = useState(VETTING_REQUEST_REASON.UNKNOWN);
  // True when a request for this email was already open and has been updated rather than
  // duplicated. The confirmation says "reminded", not "asked".
  const [vettingRepeated, setVettingRepeated] = useState(false);
  // The visit date, when the host already has one — §4.10: "send the visit date as needed_by
  // when you have one, it is what makes a 'clearance ends before the visit' request".
  const [vettingNeededBy, setVettingNeededBy] = useState("");

  const headerTitle = t("react_hi").replace("%s", getFirstName());

  useEffect(() => {
    document.title = `${t("react_visitors")} | Ronspot`;
    // `t` is a dependency on purpose: the title should follow a language change. Several
    // existing screens use `[]` here and their titles go stale after switching language.
  }, [t]);

  // ── Loading the list: server-side search, lazy-loaded pages (§4.4) ─────────────────────────
  //
  // The list is paginated and the search term goes to the server. The two go together: once only
  // one page is held in memory, a client-side filter would search the rows already fetched and
  // report "no visitors" for someone sitting on page 3.
  //
  // `loadPage` is the single entry point. `append` is what separates "the host scrolled to the
  // bottom" (add to what is there) from "the search changed" (replace it).
  //
  // The in-flight token is not optional. Typing "sarah" fires a request per debounced keystroke,
  // and those can land out of order — without it, a slow response for "sar" can overwrite the
  // results for "sarah" and the host sees rows that do not match what they typed.
  const requestToken = useRef(0);

  // Refs, not state: `fetchVisits` reads these at call time, and making them dependencies would
  // rebuild it — and re-run every effect that depends on it — on each keystroke and each page.
  const appliedQueryRef = useRef("");
  const loadedCountRef = useRef(0);
  useEffect(() => {
    appliedQueryRef.current = appliedQuery;
  }, [appliedQuery]);
  useEffect(() => {
    loadedCountRef.current = visits.length;
  }, [visits]);

  const loadPage = useCallback(
    async ({ page = 1, term = "", append = false, perPage = DEFAULT_PAGE_SIZE } = {}) => {
      const token = ++requestToken.current;

      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const { ok, response, list } = await fetchVisitList({
          Query: term,
          Page: page,
          PerPage: perPage,
        });

        // A newer request started while this one was in flight: its answer is the current truth,
        // so this one is dropped rather than painted.
        if (token !== requestToken.current) return;

        handleApiUiResponse(response, {
          successToaster: 0,
          errorToaster: 1,
          alertPopup: 1,
        });

        if (!ok) return;

        setVisits((current) => (append ? [...current, ...list.visits] : list.visits));
        setTotal(Number(list.total ?? list.visits.length));
        setPage(Number(list.page ?? page));

        // NOT APPENDED. §4.4 sends these on page 1 only, "so an app appending pages never shows
        // them twice" — so a Load more must leave what is already on screen alone rather than
        // concatenating an empty array over it, or adding the same rows again if the server ever
        // does send them twice.
        if (!append) setVettingRequests(list.vettingRequests ?? []);
      } catch (error) {
        console.error("GetVisits error", error);
      } finally {
        if (token === requestToken.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    // `handleApiUiResponse` is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Re-read after a mutation. It deliberately asks for ONE page big enough to cover everything the
  // host had already scrolled through, rather than snapping them back to the first 25 rows after
  // cancelling something on page three. Capped, because an unbounded `per_page` is a request the
  // server is entitled to refuse.
  const fetchVisits = useCallback(
    () =>
      loadPage({
        page: 1,
        term: appliedQueryRef.current,
        perPage: Math.min(
          MAX_REFRESH_PAGE_SIZE,
          Math.max(DEFAULT_PAGE_SIZE, loadedCountRef.current)
        ),
      }),
    [loadPage]
  );

  const fetchSites = useCallback(async () => {
    try {
      const { ok, sites: loaded } = await fetchSiteList();
      if (ok) setSites(loaded);
    } catch (error) {
      // Non-fatal: the wizard renders with an empty site list rather than blocking the screen.
      console.error("GetVisitorSites error", error);
    }
  }, []);

  useEffect(() => {
    if (!canSeeVisitors) return;

    fetchSites();
  }, [canSeeVisitors, fetchSites]);

  // THE SEARCH DEBOUNCE. A request per keystroke would be one per letter of "sarah" — five round
  // trips for one intention, and the first four are wasted. 350ms is long enough to swallow
  // ordinary typing and short enough not to feel laggy.
  //
  // An empty term is applied IMMEDIATELY: clearing the box is a "show me everything again" and
  // waiting a third of a second to obey it feels broken.
  useEffect(() => {
    if (!canSeeVisitors) return undefined;

    const term = query.trim();

    if (term === "") {
      setAppliedQuery("");
      return undefined;
    }

    const timer = setTimeout(() => setAppliedQuery(term), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, canSeeVisitors]);

  // One effect owns "the search changed, start again from page 1". The list drops its own
  // selection on the same signal — see the effect on `query` in VisitorList.jsx.
  useEffect(() => {
    if (!canSeeVisitors) return;

    loadPage({ page: 1, term: appliedQuery, append: false });
  }, [appliedQuery, canSeeVisitors, loadPage]);

  // `total` is everything that matched, not what is on screen — so this is the question the
  // sentinel asks.
  const hasMore = visits.length < total;

  const loadMore = useCallback(() => {
    // Guarded rather than trusted: the sentinel can fire repeatedly while it sits in view, and
    // each extra call would append the same page again.
    if (isLoading || isLoadingMore || !hasMore) return;

    loadPage({ page: page + 1, term: appliedQuery, append: true });
  }, [isLoading, isLoadingMore, hasMore, loadPage, page, appliedQuery]);

  // ── Idempotency (RON-6002 §6) ──────────────────────────────────────────────────────────────
  //
  // The API requires an `Idempotency-Key` on create, patch, cancel and group cancel: one key per
  // user ACTION, reused on retry. "These clients are a phone on a train and a laptop on hotel
  // wifi" — without a key, a lost response plus a tapped "try again" invites the party twice.
  //
  // The key therefore belongs to the FLOW, not to the request. It is minted when the user enters a
  // flow (opens the wizard, opens a cancel confirm) and every submit attempt from that screen
  // reuses it, so pressing Save again after a network failure replays rather than duplicates.
  // Re-entering the flow mints a fresh one, because that is a genuinely new action.
  //
  // A ref rather than state: nothing renders from it, and a re-render must not change it
  // mid-retry.
  //
  // NOTE for the bulk fan-out (no bulk endpoint exists — see docs/VisitorAPI.md): when bulk edit
  // and bulk cancel become N single-row calls, each row needs its OWN key. Sharing one across the
  // fan-out would make requests 2..N look like replays of request 1 and silently drop them.
  const actionKeyRef = useRef(newIdempotencyKey());

  const beginAction = () => {
    actionKeyRef.current = newIdempotencyKey();
    // Whatever the previous cancel left ticked is finished with: the host is doing something
    // else now, and the list must not re-tick those rows when they come back to it.
    setKeepTicked([]);
  };

  const goTo = (next) => () => {
    beginAction();
    setScreen(next);
  };

  const resetLookup = () => {
    setContractor(null);
    setLookup(null);
    setHasSearched(false);
    setLookupError(null);
  };

  const backToList = () => {
    setScreen(SCREENS.LIST);
    setSelectedId(null);
    setSelectedIds([]);
    setIncludeGroup(false);
    setCancelSummary(null);
    setEditSummary(null);
    resetLookup();
    setVettingEmail("");
    setVettingType(VETTING_REQUEST_REASON.UNKNOWN);
    setVettingRepeated(false);
    setVettingNeededBy("");
  };

  // Resolved from the live list, so an edit or cancel elsewhere is reflected immediately.
  //
  // HOST FALLS BACK TO THE SIGNED-IN USER when the row carries none.
  //
  // Added 07-October-2026 because `/visitor/me/visits` did not return `host` at all and the View
  // screen, which is fed from this list, rendered a BLANK Host row. The Laravel team added the
  // field the same day, so the normal path is now the server's own value.
  //
  // THIS IS STILL REACHED, and deliberately: their note says `host` can be `null` — "`HostUserID`
  // carries no foreign key, so a visit can point at a user row that is no longer there — branch
  // on it rather than reading `host.name` directly." On these endpoints the host is always the
  // caller, so the signed-in name is the right answer for that row rather than an empty one.
  //
  // Not to be confused with who BOOKED the visit: reception or an admin can book naming you as
  // host, and `host` is still you on that row. That is `created_by`, which we do not receive —
  // see RON-6002_open-questions.md if the screen ever needs to show it.
  const selectedVisit = useMemo(() => {
    const found = visits.find((visit) => visit.id === selectedId) || null;

    if (!found) return null;

    return found.host ? found : { ...found, host: getHostName() || null };
  }, [visits, selectedId]);

  const selectedVisits = useMemo(
    () => visits.filter((visit) => selectedIds.includes(visit.id)),
    [visits, selectedIds]
  );

  // Other people invited in the same submission. Only cancellable ones are offered — listing
  // siblings that are already closed would just be noise on a confirmation screen.
  const groupSiblings = useMemo(() => {
    if (!selectedVisit?.group) return [];

    return visits.filter(
      (visit) =>
        visit.group === selectedVisit.group &&
        visit.id !== selectedVisit.id &&
        canCancelVisit(visit)
    );
  }, [visits, selectedVisit]);

  // Everyone else on the same booking, whatever their status — the View screen lists them so the
  // host can see who an edit or cancel here sits alongside. Distinct from `groupSiblings`, which is
  // filtered to the cancellable ones because it drives the cancel screen's opt-in.
  const groupMembers = useMemo(() => {
    if (!selectedVisit?.group) return [];

    return visits.filter(
      (visit) => visit.group === selectedVisit.group && visit.id !== selectedVisit.id
    );
  }, [visits, selectedVisit]);

  const openSingle = (next) => (visit) => {
    beginAction();
    setSelectedId(visit.id);
    setIncludeGroup(false);
    resetLookup();

    // Editing a contractor re-reads the vetting register. Vetting changes between booking and
    // editing — someone cleared in August may be blocked by October — and the wizard falls back to
    // the visit's stored snapshot only if this cannot be reached, so the gate is never simply lost.
    if (next === SCREENS.EDIT && visit.kind === VISIT_KIND.CONTRACTOR && visit.email) {
      lookupContractor(visit.email);
    }

    setScreen(next);
  };

  const openBulk = (next) => (ids) => {
    beginAction();
    setSelectedIds(ids);
    setScreen(next);
  };

  const submitVisitorInvite = async (payload) => {
    try {
      setIsSubmitting(true);

      const response = await CreateVisit_api({
        Kind: payload.kind,
        Visitors: payload.visitors,
        SiteID: payload.site?.id ?? null,
        BuildingID: payload.building?.id ?? null,
        StartDate: payload.startDate,
        EndDate: payload.endDate,
        ArrivalTime: payload.arrivalTime,
        HostName: payload.host,
        Type: payload.type,
        Resources: payload.resources,
        SendEmail: payload.sendEmail ? 1 : 0,
        IdempotencyKey: actionKeyRef.current,
      });

      handleApiUiResponse(response, {
        successToaster: 0,
        errorToaster: 1,
        alertPopup: 1,
      });

      // Only advance on success — a failed submit must keep the user on the review step with
      // their input intact rather than showing a confirmation for a visit that was never created.
      // handleApiUiResponse displays the error but returns nothing, hence the explicit check.
      if (!isSuccessfulResponse(response)) return;

      await fetchVisits();
      setScreen(SCREENS.VISITOR_INVITED);
    } catch (error) {
      console.error("CreateVisit error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shared tail for every mutation: report, bail on failure, re-read, advance. Written once
  // because "only advance on success" is the rule that keeps a failed save from showing a
  // confirmation for something that never happened.
  const runMutation = async (label, call, onSuccess) => {
    try {
      setIsSubmitting(true);

      const response = await call();

      handleApiUiResponse(response, {
        successToaster: 0,
        errorToaster: 1,
        alertPopup: 1,
      });

      if (!isSuccessfulResponse(response)) return;

      await fetchVisits();
      onSuccess?.(response?.Data?.Records || {});
    } catch (error) {
      console.error(`${label} error`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit one visit. The wizard hands back its whole form; only the visit's own fields are sent.
  const submitVisitEdit = (payload) =>
    runMutation(
      "UpdateVisit",
      () =>
        UpdateVisit_api({
          VisitID: payload.visitId,
          Name: payload.visitors[0]?.name,
          Email: payload.visitors[0]?.email,
          Company: payload.visitors[0]?.company,
          StartDate: payload.startDate,
          EndDate: payload.endDate,
          ArrivalTime: payload.arrivalTime,
          // Stored by name, matching what the list and detail screens display.
          Site: payload.site?.name ?? null,
          Building: payload.building?.name ?? null,
          HostName: payload.host,
          Type: payload.type,

          // PATCH carries all five as of 14-October, which is what let step 3 back into the edit
          // flow. Partial, so only what changed — but the wizard sends the whole form, and the
          // server treats an unchanged value as unchanged.
          WorkDescription: payload.workDescription || undefined,
          Areas: payload.areas || undefined,
          Duration: payload.duration || undefined,
          Supervisor: payload.supervisor || undefined,
          Personnel: payload.personnel ? Number(payload.personnel) : undefined,

          SendEmail: payload.sendEmail ? 1 : 0,
          IdempotencyKey: actionKeyRef.current,
        }),
      () => setScreen(SCREENS.CHANGES_SAVED)
    );

  // Bulk edit. `changes` holds ONLY the fields the host touched, so nothing else is overwritten.
  const submitBulkEdit = (changes, sendEmail = true) =>
    runMutation(
      "UpdateVisit (bulk)",
      () =>
        UpdateVisit_api({
          VisitIDs: selectedIds,
          // Mapped explicitly rather than spread, so a new form field cannot silently become a
          // writable column.
          ...(changes.startDate !== undefined ? { StartDate: changes.startDate } : {}),
          ...(changes.endDate !== undefined ? { EndDate: changes.endDate } : {}),
          ...(changes.arrivalTime !== undefined
            ? { ArrivalTime: changes.arrivalTime }
            : {}),
          ...(changes.site !== undefined ? { Site: changes.site } : {}),
          ...(changes.building !== undefined ? { Building: changes.building } : {}),
          ...(changes.host !== undefined ? { HostName: changes.host } : {}),
          // §7. One `visit_updated` per visit, and only for those whose visible details changed.
          SendEmail: sendEmail ? 1 : 0,
          IdempotencyKey: actionKeyRef.current,
        }),
      (records) => {
        const summary = bulkEditFromApi(records);

        setEditSummary(summary);
        // The rows that did NOT save stay ticked, so a second Save retries only those rather
        // than patching the ones already correct all over again.
        setKeepTicked(summary.results.filter((row) => !row.ok).map((row) => row.id));
        setScreen(SCREENS.CHANGES_SAVED);
      }
    );

  // A miss is a normal outcome, not an error, so this does NOT go through `runMutation`: there is
  // no toast to show and nothing to advance to. `hasSearched` is what tells the step apart from
  // "not searched yet".
  const lookupContractor = async (email) => {
    try {
      setIsSearching(true);
      setLookupError(null);

      const {
        ok,
        response,
        contractor: found,
        lookup: verdict,
      } = await lookupContractorApi({ Email: email });

      if (!ok) {
        handleApiUiResponse(response, { successToaster: 0, errorToaster: 1, alertPopup: 1 });
        setLookupError("react_visitors_error_lookup_failed");
        return;
      }

      // Null means "searched, and not on the register" — a real answer, not a failure, and the
      // one that sends the host down the vetting path instead of the booking one.
      setContractor(found);
      setLookup(verdict);
      setHasSearched(true);
    } catch (error) {
      console.error("LookupContractor error", error);
      setLookupError("react_visitors_error_lookup_failed");
    } finally {
      setIsSearching(false);
    }
  };

  // UNGATED 29-October-2026. §4.10 shipped, so this is a permission now, not a workaround.
  //
  // It was `USE_VISITOR_MOCKS` from September, when the contract had nine endpoints and none
  // of them could put somebody on the register. Laravel's answer was "park screens 27 and 28
  // behind a flag", and the flag was the mock switch — hidden rather than shown-and-failing,
  // because a host told "we'll be in touch" waits for a vetting nobody was ever asked for and
  // finds out when their contractor is turned away at the door.
  //
  // `can_contractor` is the endpoint's own gate: a host with visitors only gets 403 from both
  // the register and this, so the button and the call agree about who may use them.
  const canRequestVetting = canInviteContractor;

  const openRequestVetting = (email, neededBy) => {
    if (!canRequestVetting) return;

    beginAction();
    setVettingEmail(email || "");
    setVettingNeededBy(neededBy || "");
    setScreen(SCREENS.REQUEST_VETTING);
  };

  // Sends a vetting request and stops. It deliberately does NOT create a visit: approval has to
  // complete first, otherwise an unvetted contractor ends up booked onto a site.
  const submitVettingRequest = (payload) =>
    runMutation(
      "RequestContractorVetting",
      () =>
        RequestContractorVetting_api({
          Email: payload.email,
          // `ContactName` → `contact_name` (§4.10). It was `Name` for a day, which the endpoint
          // ignores — the compliance admin would have seen a request from a company with nobody
          // attached, and nothing on either side saying a field had been dropped.
          ContactName: payload.name,
          Company: payload.company,
          Trade: payload.trade,
          Reason: payload.reason,
          // `needed_by` in the shape Laravel agreed on 23-Sep — see docs/RON-6002_open-questions.md
          // Q3. Optional, so an empty string rather than a missing key keeps the payload one shape.
          NeededBy: payload.neededBy,
          IdempotencyKey: actionKeyRef.current,
        }),
      (records) => {
        // The server classifies the request from the register; the four types are the
        // prototype's own. An unrecognised one falls back inside
        // `getVettingRequestedMessageKey`.
        setVettingType(records.type);
        setVettingRepeated(records.repeated === true);
        setScreen(SCREENS.VETTING_REQUESTED);
      }
    );

  const submitContractorInvite = (payload) =>
    runMutation(
      "CreateVisit (contractor)",
      () =>
        CreateVisit_api({
          Kind: payload.kind,
          ContractorID: payload.contractorId,
          Visitors: payload.visitors,
          Trade: payload.trade,
          IsProvisional: payload.isProvisional ? 1 : 0,
          VettedUntil: payload.vettedUntil,
          SiteID: payload.site?.id ?? null,
          BuildingID: payload.building?.id ?? null,
          StartDate: payload.startDate,
          EndDate: payload.endDate,
          ArrivalTime: payload.arrivalTime,
          HostName: payload.host,
          Type: payload.type,
          Resources: payload.resources,
          SendEmail: payload.sendEmail ? 1 : 0,

          // Step 3, "Nature of work". ALL FIVE TRAVEL as of 16-October-2026 — Laravel added the
          // read, the PATCH, and columns for `duration` and `supervisor` on 14-October
          // (answers-to-frontend-8). Two of these were drawn but discarded until then.
          WorkDescription: payload.workDescription || undefined,
          Areas: payload.areas || undefined,
          Duration: payload.duration || undefined,
          Supervisor: payload.supervisor || undefined,
          // Coerced once, here. The field holds a string so it can be cleared while typing.
          Personnel: payload.personnel ? Number(payload.personnel) : undefined,

          IdempotencyKey: actionKeyRef.current,
        }),
      () => setScreen(SCREENS.VISITOR_INVITED)
    );

  // Same target as submitVisitEdit, plus the columns only a contractor has. Kept separate rather
  // than branching inside one handler, so neither payload can pick up the other's fields.
  const submitContractorEdit = (payload) =>
    runMutation(
      "UpdateVisit (contractor)",
      () =>
        UpdateVisit_api({
          VisitID: payload.visitId,
          Name: payload.visitors[0]?.name,
          Email: payload.visitors[0]?.email,
          Company: payload.visitors[0]?.company,
          Trade: payload.trade,
          IsProvisional: payload.isProvisional ? 1 : 0,
          VettedUntil: payload.vettedUntil,
          StartDate: payload.startDate,
          EndDate: payload.endDate,
          ArrivalTime: payload.arrivalTime,
          Site: payload.site?.name ?? null,
          Building: payload.building?.name ?? null,
          HostName: payload.host,
          Type: payload.type,

          // PATCH carries all five as of 14-October, which is what let step 3 back into the edit
          // flow. Partial, so only what changed — but the wizard sends the whole form, and the
          // server treats an unchanged value as unchanged.
          WorkDescription: payload.workDescription || undefined,
          Areas: payload.areas || undefined,
          Duration: payload.duration || undefined,
          Supervisor: payload.supervisor || undefined,
          Personnel: payload.personnel ? Number(payload.personnel) : undefined,

          SendEmail: payload.sendEmail ? 1 : 0,
          IdempotencyKey: actionKeyRef.current,
        }),
      () => setScreen(SCREENS.CHANGES_SAVED)
    );

  const confirmCancel = () => {
    const ids = selectedVisit
      ? [
          selectedVisit.id,
          ...(includeGroup ? groupSiblings.map((visit) => visit.id) : []),
        ]
      : selectedIds;

    // WHICH ENDPOINT — decided here, because only this screen knows what the host asked for.
    //
    // Until 02-November-2026 this sent `VisitIDs` and nothing else, and `CancelVisit_api`'s live
    // branch reads `VisitID`. So every real cancel built `/visits/undefined/cancel` and came back
    // 404 "That invitation could not be found": single, party and bulk alike. The mock's
    // `resolveTargets` accepts all three spellings and loops the fixture, so the whole suite
    // passed over a feature that had never once worked against the API.
    //
    //   one visit            §4.8  POST /visits/{id}/cancel
    //   the whole party      §4.9  POST /visit-groups/{group_id}/cancel — partial, and it says so
    //
    // There is no endpoint for an arbitrary set of ids across groups, which is what a bulk
    // selection is. That case is still broken and is with the Laravel team.
    const cancelWholeParty = Boolean(
      selectedVisit && includeGroup && selectedVisit.group
    );

    return runMutation(
      "CancelVisit",
      () =>
        CancelVisit_api({
          VisitID: selectedVisit && !cancelWholeParty ? selectedVisit.id : undefined,
          GroupID: cancelWholeParty ? selectedVisit.group : undefined,
          // Still sent: the mock cancels from this, and it is what the bulk path needs the day
          // there is an endpoint behind it.
          VisitIDs: ids,
          // §8/§9 — the toggle. The server defaults it to true, so this only ever turns it off.
          SendCancellationEmail: sendCancelEmail,
          IdempotencyKey: actionKeyRef.current,
        }),
      (records) => {
        // FIXED 24-October-2026. This read `records.CancelledCount` and `records.SkippedCount`,
        // which NOTHING has ever sent — the API answers `cancelled` and `skipped` (§4.9). Both
        // fell back to 0, so the partial-cancel summary the whole screen exists to show always
        // read "0 cancelled" however many had actually been cancelled.
        //
        // Reading it through the mapper rather than inline, so the wire names live in one place
        // and this cannot drift again.
        const summary = groupCancelFromApi(records);

        setCancelSummary(summary);
        // §9a only — §8 and §9 send no `results`, so this is empty and the list clears as before.
        setKeepTicked(
          summary.results.filter((row) => !row.cancelled).map((row) => row.id)
        );
        setScreen(SCREENS.VISITS_CANCELLED);
      }
    );
  };

  // The wizard owns its own heading; the Phase 3 screens are plain cards, so the heading is added
  // here rather than repeated in three components.
  const withHeading = (node) => (
    <>
      <h2 className="module-title mb-3">{t(SCREEN_HEADING_KEYS[screen])}</h2>
      {node}
    </>
  );

  const renderScreen = () => {
    if (screen === SCREENS.LIST) {
      return (
        <VisitorList
          visits={visits}
          isLoading={isLoading}
          // Search and paging are owned here, not by the list: the term goes to the server, so
          // the component that talks to the API has to hold it.
          query={query}
          onQueryChange={setQuery}
          isSearching={query.trim() !== appliedQuery}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          total={total}
          vettingRequests={vettingRequests}
          onViewDeclined={setDeclinedInView}
          canInviteVisitor={canInviteVisitor}
          canInviteContractor={canInviteContractor}
          onInviteVisitor={goTo(SCREENS.INVITE_VISITOR)}
          onInviteContractor={goTo(SCREENS.INVITE_CONTRACTOR)}
          onView={openSingle(SCREENS.VIEW)}
          onEdit={openSingle(SCREENS.EDIT)}
          onCancel={openSingle(SCREENS.CANCEL)}
          onBulkEdit={openBulk(SCREENS.BULK_EDIT)}
          onBulkCancel={openBulk(SCREENS.BULK_CANCEL)}
          // THE TICKS LIVE IN THE LIST, not here — `selectedIds` above is only the copy it
          // hands up when an action starts. So the rows a bulk cancel could not cancel are
          // given back to the list to re-tick itself, rather than written to that copy, which
          // renders nothing.
          restoreSelectedIds={keepTicked}
        />
      );
    }

    if (screen === SCREENS.INVITE_VISITOR) {
      return (
        <VisitorInviteWizard
          sites={sites}
          todayISO={toISODate(new Date())}
          hostName={getHostName()}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
          isSubmitting={isSubmitting}
          onSubmit={submitVisitorInvite}
          onCancel={backToList}
        />
      );
    }

    if (screen === SCREENS.INVITE_CONTRACTOR) {
      return (
        <ContractorInviteWizard
          sites={sites}
          todayISO={toISODate(new Date())}
          hostName={getHostName()}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
          isSubmitting={isSubmitting}
          isSearching={isSearching}
          contractor={contractor}
          hasSearched={hasSearched}
          lookupResult={lookup}
          lookupError={lookupError}
          onLookup={lookupContractor}
          onResetLookup={resetLookup}
          onRequestVetting={canRequestVetting ? openRequestVetting : undefined}
          onSubmit={submitContractorInvite}
          onCancel={backToList}
        />
      );
    }

    if (screen === SCREENS.REQUEST_VETTING) {
      return withHeading(
        <ContractorRequestVetting
          initialEmail={vettingEmail}
          initialNeededBy={vettingNeededBy}
          isSubmitting={isSubmitting}
          onSubmit={submitVettingRequest}
          // Back to the wizard, not the list: the host was mid-invite.
          onBack={() => setScreen(SCREENS.INVITE_CONTRACTOR)}
        />
      );
    }

    if (screen === SCREENS.VETTING_REQUESTED) {
      return (
        <VisitorConfirmation
          titleKey="react_visitors_vetting_requested_title"
          messageKey={getVettingRequestedMessageKey(vettingType, vettingRepeated)}
          // The address it went to, under the title. A host who mistyped finds out here rather
          // than a week later when nobody has been vetted.
          subtitle={vettingEmail}
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.VIEW) {
      return (
        <VisitorDetail
          visit={selectedVisit}
          groupMembers={groupMembers}
          onEdit={openSingle(SCREENS.EDIT)}
          onCancel={openSingle(SCREENS.CANCEL)}
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.EDIT) {
      // Contractors get their own wizard, because their edit is gated on vetting and the visitor
      // wizard has no such gate — routing them through it let a host move a contractor's dates
      // past their clearance unchallenged.
      if (selectedVisit?.kind === VISIT_KIND.CONTRACTOR) {
        return (
          <ContractorInviteWizard
            mode="edit"
            initialVisit={selectedVisit}
            sites={sites}
            todayISO={toISODate(new Date())}
            hostName={getHostName()}
            canAssignDesk={canAssignDesk}
            canAssignParking={canAssignParking}
            isSubmitting={isSubmitting}
            isSearching={isSearching}
            contractor={contractor}
            hasSearched={hasSearched}
            lookupResult={lookup}
            lookupError={lookupError}
            onLookup={lookupContractor}
            onResetLookup={resetLookup}
            onRequestVetting={canRequestVetting ? openRequestVetting : undefined}
            onSubmit={submitContractorEdit}
            onCancel={backToList}
          />
        );
      }

      return (
        <VisitorInviteWizard
          mode="edit"
          initialVisit={selectedVisit}
          sites={sites}
          todayISO={toISODate(new Date())}
          hostName={getHostName()}
          canAssignDesk={canAssignDesk}
          canAssignParking={canAssignParking}
          isSubmitting={isSubmitting}
          onSubmit={submitVisitEdit}
          onCancel={backToList}
        />
      );
    }

    if (screen === SCREENS.CANCEL || screen === SCREENS.BULK_CANCEL) {
      return withHeading(
        <VisitorCancelConfirm
          sendEmail={sendCancelEmail}
          onToggleEmail={() => setSendCancelEmail((current) => !current)}
          // One screen for both: a single cancel is a batch of one.
          visits={selectedVisit ? [selectedVisit] : selectedVisits}
          groupSiblings={screen === SCREENS.CANCEL ? groupSiblings : []}
          includeGroup={includeGroup}
          // The chooser answers once; it does not flip a flag the host can toggle back, so the
          // screen hands up the scope it was given rather than asking for the opposite of now.
          onSelectGroup={setIncludeGroup}
          isBulk={screen === SCREENS.BULK_CANCEL}
          isSubmitting={isSubmitting}
          onConfirm={confirmCancel}
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.BULK_EDIT) {
      return withHeading(
        <VisitorBulkEditScreen
          visits={selectedVisits}
          sites={sites}
          isSubmitting={isSubmitting}
          onSubmit={submitBulkEdit}
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.VISITOR_INVITED) {
      return (
        <VisitorConfirmation
          titleKey="react_visitors_invited_title"
          messageKey="react_visitors_invited_message"
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.CHANGES_SAVED) {
      // A single edit has no summary and reads as it always did. Only a bulk edit can land
      // half-applied, because only a bulk edit is N requests.
      const nothingSaved = editSummary ? editSummary.updated === 0 : false;
      const someFailed = (editSummary?.failed ?? 0) > 0;

      const failedRows = (editSummary?.results ?? [])
        .filter((row) => !row.ok)
        .map((row) => ({
          id: row.id,
          name:
            visits.find((visit) => visit.id === row.id)?.name ??
            t("react_visitors_unnamed"),
          message: row.message,
        }));

      return (
        <VisitorConfirmation
          titleKey={
            nothingSaved
              ? "react_visitors_saved_none_title"
              : "react_visitors_saved_title"
          }
          messageKey={
            nothingSaved
              ? "react_visitors_saved_none_message"
              : someFailed
              ? pluralKey("react_visitors_saved_message_some", editSummary?.updated ?? 0)
              : "react_visitors_saved_message"
          }
          messageValue={someFailed ? String(editSummary?.updated ?? 0) : undefined}
          rows={failedRows}
          rowsLabel={t("react_visitors_not_updated")}
          onBack={backToList}
        />
      );
    }

    if (screen === SCREENS.VISITS_CANCELLED) {
      const cancelledCount = cancelSummary?.cancelled ?? 0;
      const nothingCancelled = cancelledCount === 0;

      // One line per row the server would not cancel, named and explained. `results` is §9a's,
      // so this is empty for a single or party cancel and the screen reads as it always did.
      //
      // The name comes from the list rather than the reply: §9a answers with ids, and "v-7c2a…
      // could not be cancelled" is not something to put in front of a host.
      const keptRows = (cancelSummary?.results ?? [])
        .filter((row) => !row.cancelled)
        .map((row) => ({
          id: row.id,
          name:
            visits.find((visit) => visit.id === row.id)?.name ??
            t("react_visitors_unnamed"),
          // THE SERVER'S ENGLISH, deliberately, until the wording policy is settled: it is the
          // fallback either way, so showing it now needs no rework. If we key our own strings
          // off `row.reason`, they slot in front of this and this stays as the unknown case.
          message: row.message,
        }));

      return (
        <VisitorConfirmation
          variant="cancel"
          titleKey={
            // NOT "Bookings cancelled" when none were. A selection where every row was already
            // closed is not a success, and saying so while the rows sit there untouched is how
            // a host stops trusting the screen.
            nothingCancelled
              ? "react_visitors_cancelled_none_title"
              : pluralKey("react_visitors_cancelled_title", cancelledCount)
          }
          messageKey={
            nothingCancelled
              ? "react_visitors_cancelled_none_message"
              : pluralKey(
                  // §9 and §9a are partial by design: say what was left alone rather than
                  // reporting a bare success for a selection only half acted on.
                  cancelSummary?.skipped
                    ? "react_visitors_cancelled_message_skipped"
                    : "react_visitors_cancelled_message",
                  cancelledCount
                )
          }
          messageValue={String(cancelledCount)}
          rows={keptRows}
          rowsLabel={t("react_visitors_cancel_kept")}
          onBack={backToList}
        />
      );
    }

    return (
      <div className="visitor">
        <h2 className="module-title mb-3">
          {t(SCREEN_TITLE_KEYS[screen] || "react_visitors")}
        </h2>
        <div className="visitor__placeholder">{t("react_visitors_wip")}</div>
        <div className="visitor__placeholder-actions">
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={backToList}
          >
            {t("react_visitors_back_to_list")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="content-area visitors-page">
      {renderApiUi(isLoading)}

      <header className="header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-3">
              <div className="greeting-title">
                <p>{headerTitle}</p>
              </div>
            </div>

            <div className="col-md-6"></div>

            <div className="col-md-3">
              <div className="menu-bar-options d-flex justify-content-end gap-3">
                {/* No CreditInfoCard: visitor invites do not consume the employee's credits.
                    Desk/parking booked for a guest goes through Ronspot's own booking flow. */}
                <div className="notification-info">
                  {/* href points at the offcanvas this toggles rather than the bare "#" other
                      screens use: same styling (CSS targets `.notification-info a`), but a valid
                      fragment, so it does not trip jsx-a11y/anchor-is-valid. */}
                  <a
                    href="#notification-panel"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#notification-panel"
                    aria-controls="notification-panel"
                    className="position-relative notification-icon"
                  >
                    <img src={Images.notification} alt="" />
                    {hasUnreadNotifications && <span className="orange-dot"></span>}
                  </a>
                </div>
                <NotificationPanel onUnreadChange={setHasUnreadNotifications} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="component-area visitors-page">
        <Sidebar />

        <div className="pt-96 pb-96 my-container active-cont">
          <div className="container mw-100 pt-30">
            <div className="row">
              <div className="col-md-12">
                {canSeeVisitors ? (
                  renderScreen()
                ) : (
                  // Reached only by direct URL — the Sidebar hides the item when the company
                  // has no `visitor_portal` or the employee has no invite right. Deliberately a
                  // plain message, not an upsell: upselling is an admin-side surface per the epic.
                  <div className="visitor__unavailable">
                    <p className="visitor__unavailable-text">
                      {t("react_visitors_unavailable")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Outside the scrolling container so the backdrop covers the page rather than one column.
          Rendered only when a request is open — the component returns null on a null request
          anyway, but not mounting it keeps the backdrop out of the DOM entirely. */}
      {declinedInView && (
        <VisitorVettingDeclinedModal
          request={declinedInView}
          onClose={() => setDeclinedInView(null)}
        />
      )}
    </div>
  );
};

export default VisitorUI;
